/**
 * Training Break System Types
 *
 * Covers Travel Mode and Injury/Deload Recovery.
 * Both share a common "Training Break" model — the difference
 * is the reason and the behavior during/after the break.
 */

// ============================================================================
// CORE TYPES
// ============================================================================

export type BreakReason = 'travel' | 'injury' | 'deload' | 'personal';

export type InjuryLocation =
  | 'shoulder' | 'elbow' | 'wrist' | 'hand'
  | 'upper-back' | 'lower-back' | 'neck'
  | 'hip' | 'knee' | 'ankle' | 'foot'
  | 'chest' | 'quad' | 'hamstring' | 'calf' | 'shin'
  | 'general';

export type InjurySeverity = 'mild' | 'moderate' | 'severe';

export type ReturnPhase = 'rest' | 'light' | 'moderate' | 'full';

// ============================================================================
// TRAVEL TYPES
// ============================================================================

export interface TravelExercise {
  exerciseName: string;
  equipment: string;
  sets: number;
  reps: string;          // "10" or "12-15" or "AMRAP"
  rest: string;          // "60s"
  notes?: string;
}

export interface TravelWorkout {
  dayIndex: number;
  title: string;         // "Day 1: Upper Body Push/Pull"
  focus: string;         // "chest, back, shoulders"
  exercises: TravelExercise[];
  estimatedMinutes: number;
}

export interface TravelConfig {
  equipment: string[];              // equipment IDs selected
  workoutDays: number;              // how many days to generate
  generatedWorkouts: TravelWorkout[];
  completedDayIndices: number[];
}

// ============================================================================
// INJURY TYPES
// ============================================================================

export interface PreInjuryAnalysis {
  volumeTrend7d: number;            // % change last 7 days vs prior 7 days
  acwrAtInjury: number;
  highRPESessions: number;          // count of RPE >= 8 in last 7 days
  suspectedExercises: string[];     // exercises done in 48hrs before injury
  recoveryScoreAtInjury: number;
}

export interface ReturnToTraining {
  phase: ReturnPhase;
  intensityPct: number;             // 0 -> 50 -> 75 -> 100
  volumePct: number;
  phaseDaysRemaining: number;
}

export interface InjuryConfig {
  location: InjuryLocation;
  side?: 'left' | 'right' | 'both';
  severity: InjurySeverity;
  description: string;              // user's own words
  avoidExercises: string[];         // auto + manual exercise IDs to avoid
  avoidMuscleGroups: string[];      // muscle groups to protect
  preInjuryAnalysis?: PreInjuryAnalysis;
  returnToTraining?: ReturnToTraining;
}

// ============================================================================
// PAUSED PROGRAM SNAPSHOT
// ============================================================================

export interface PausedProgramSnapshot {
  programId: string;
  programLevel: string;
  startDate?: string;              // original program start date (ISO)
  currentWeek: number;
  currentDay: number;
  completedDays: { week: number; day: number }[];
  skippedDays: { week: number; day: number }[];
}

// ============================================================================
// MAIN TRAINING BREAK MODEL
// ============================================================================

export interface TrainingBreak {
  id: string;
  userId: string;
  reason: BreakReason;
  startDate: string;                // ISO date YYYY-MM-DD
  endDate: string;                  // projected return date
  isActive: boolean;

  pausedProgram: PausedProgramSnapshot | null;

  travel?: TravelConfig;
  injury?: InjuryConfig;

  createdAt: string;                // ISO datetime
  resolvedAt?: string;              // when break ended
}

// ============================================================================
// EQUIPMENT PICKER TYPES
// ============================================================================

export interface EquipmentOption {
  id: string;
  label: string;
  icon: string;
  dbMatchValues: string[];          // maps to exercise DB `equipment` field values
  alwaysOn?: boolean;               // bodyweight is always included
}

/**
 * Equipment tokens, matched EXACTLY against `Exercise.equipment` values.
 *
 * These were written from imagination rather than from the data, so several
 * named values that do not exist in the database at all ('cardio',
 * 'stationary bike', 'elliptical', 'e-z curl bar', 'chin-up bar'), while real
 * values went unmatched because of singular/plural and spacing differences.
 * The result: selecting equipment barely changed the generated plan.
 *
 * Concretely, before this was corrected:
 *   - "Dumbbells" missed the 18 exercises tagged `dumbbells` (plural)
 *   - "Barbell & Rack" missed `ez-bar` (9), `ez bar` (2), `squat rack` (3),
 *     `trap bar` and `landmine`
 *   - "Cable Machine" missed the 12 tagged `cable machine`
 *   - "Treadmill / Bike" reached exactly 2 exercises; `assault bike` was
 *     unreachable
 *   - "Resistance Bands" missed `resistance band`
 *   - "Pull-Up Bar" missed `dip bars`, `rings`, `parallettes`, `gravitron`
 *     and `assisted pull-up machine`
 *
 * Every value below is present in the merged database — pinned by
 * lib/__tests__/travelEquipmentTokens.test.ts, so a future edit that invents
 * a token fails loudly instead of silently narrowing the pool.
 */
export const EQUIPMENT_OPTIONS: EquipmentOption[] = [
  // 'body weight' (with a space) does NOT exist in the database — the value is
  // `bodyweight`. The old token used the spaced form, which matched nothing;
  // bodyweight exercises only survived because filterExercisesByEquipment
  // separately lets through entries with an empty equipment list.
  { id: 'bodyweight', label: 'Bodyweight', icon: '🏋️', dbMatchValues: ['bodyweight', 'none'], alwaysOn: true },
  { id: 'dumbbells', label: 'Dumbbells', icon: '🔩', dbMatchValues: ['dumbbell', 'dumbbells'] },
  { id: 'barbell', label: 'Barbell & Rack', icon: '🏗️', dbMatchValues: ['barbell', 'ez-bar', 'ez bar', 'squat rack', 'trap bar', 'landmine', 'weight belt'] },
  { id: 'cable', label: 'Cable Machine', icon: '🔧', dbMatchValues: ['cable', 'cable machine'] },
  { id: 'machine', label: 'Weight Machines', icon: '⚙️', dbMatchValues: ['machine'] },
  { id: 'kettlebell', label: 'Kettlebell', icon: '🔔', dbMatchValues: ['kettlebell'] },
  { id: 'cardio', label: 'Treadmill / Bike', icon: '🏃', dbMatchValues: ['treadmill', 'assault bike'] },
  { id: 'bands', label: 'Resistance Bands', icon: '〰️', dbMatchValues: ['band', 'resistance band'] },
  { id: 'pullup-bar', label: 'Pull-Up Bar', icon: '💪', dbMatchValues: ['pull-up bar', 'dip bars', 'rings', 'parallettes', 'gravitron', 'assisted pull-up machine'] },
  { id: 'bench', label: 'Bench', icon: '🪑', dbMatchValues: ['bench', 'flat bench', 'incline bench', 'stability ball', 'box'] },
];

// ============================================================================
// INJURY LOCATION → EXERCISE AVOIDANCE MAP
// ============================================================================

/** Severity-aware extension: exercises added ONLY at severe level */
export const SEVERE_ADDITIONAL_AVOID: Partial<Record<InjuryLocation, string[]>> = {
  shoulder: ['bench press', 'incline bench press', 'dips', 'dumbbell fly', 'cable fly'],
  elbow: ['bench press', 'push-ups', 'dips', 'tricep dip', 'hammer curl'],
  wrist: ['dumbbell press', 'kettlebell swing', 'dips'],
  knee: ['step up', 'leg press', 'calf raise', 'cycling'],
  'lower-back': ['squat', 'front squat', 'overhead press', 'military press'],
  hip: ['leg press', 'abductor machine', 'adductor machine'],
  ankle: ['squat', 'lunge', 'walking lunge', 'leg press'],
};

export const INJURY_AVOID_MAP: Record<InjuryLocation, {
  exercises: string[];
  muscleGroups: string[];
  substitutes: Record<string, string>;
}> = {
  shoulder: {
    exercises: ['overhead press', 'military press', 'barbell overhead press', 'upright row', 'lateral raise', 'arnold press', 'behind the neck press'],
    muscleGroups: ['shoulders'],
    substitutes: { 'overhead press': 'landmine press', 'lateral raise': 'face pull', 'upright row': 'face pull' },
  },
  elbow: {
    exercises: ['skull crusher', 'close grip bench press', 'barbell curl', 'preacher curl', 'concentration curl'],
    muscleGroups: [],
    substitutes: { 'skull crusher': 'cable pushdown', 'barbell curl': 'cable curl' },
  },
  wrist: {
    exercises: ['barbell curl', 'push-ups', 'front squat', 'clean'],
    muscleGroups: [],
    substitutes: { 'barbell curl': 'hammer curl', 'push-ups': 'dumbbell press' },
  },
  hand: {
    exercises: ['deadlift', 'pull-ups', 'barbell row', 'farmer walk'],
    muscleGroups: [],
    substitutes: {},
  },
  'upper-back': {
    exercises: ['barbell row', 'deadlift', 'pull-ups', 'lat pulldown'],
    muscleGroups: ['back'],
    substitutes: { 'barbell row': 'chest supported row', 'deadlift': 'leg press' },
  },
  'lower-back': {
    exercises: ['deadlift', 'good morning', 'barbell row', 'romanian deadlift', 'bent over row', 'back squat'],
    muscleGroups: [],
    substitutes: { 'deadlift': 'trap bar deadlift', 'barbell row': 'chest supported row', 'back squat': 'leg press' },
  },
  neck: {
    exercises: ['overhead press', 'shrugs', 'upright row'],
    muscleGroups: [],
    substitutes: {},
  },
  hip: {
    exercises: ['squat', 'deadlift', 'lunge', 'hip thrust', 'running'],
    muscleGroups: ['glutes'],
    substitutes: { 'squat': 'leg press', 'lunge': 'step up' },
  },
  knee: {
    exercises: ['squat', 'back squat', 'front squat', 'lunge', 'walking lunge', 'box jump', 'running', 'leg extension', 'jump squat'],
    muscleGroups: ['quads'],
    substitutes: { 'squat': 'leg press', 'lunge': 'step up', 'box jump': 'glute bridge' },
  },
  ankle: {
    exercises: ['running', 'box jump', 'jump squat', 'calf raise', 'jump rope'],
    muscleGroups: ['calves'],
    substitutes: { 'running': 'cycling', 'box jump': 'glute bridge' },
  },
  foot: {
    exercises: ['running', 'box jump', 'calf raise', 'jump rope', 'ruck march'],
    muscleGroups: [],
    substitutes: { 'running': 'cycling' },
  },
  chest: {
    exercises: ['bench press', 'incline bench press', 'dips', 'push-ups', 'dumbbell fly'],
    muscleGroups: ['chest'],
    substitutes: { 'bench press': 'cable crossover' },
  },
  quad: {
    exercises: ['squat', 'leg extension', 'lunge', 'leg press', 'box jump'],
    muscleGroups: ['quads'],
    substitutes: { 'squat': 'glute bridge', 'leg extension': 'hamstring curl' },
  },
  hamstring: {
    exercises: ['deadlift', 'romanian deadlift', 'hamstring curl', 'good morning', 'running'],
    muscleGroups: ['hamstrings'],
    substitutes: { 'deadlift': 'leg press', 'hamstring curl': 'glute bridge' },
  },
  calf: {
    exercises: ['calf raise', 'running', 'box jump', 'jump rope'],
    muscleGroups: ['calves'],
    substitutes: { 'running': 'cycling' },
  },
  shin: {
    exercises: ['running', 'box jump', 'jump rope', 'ruck march'],
    muscleGroups: [],
    substitutes: { 'running': 'cycling' },
  },
  general: {
    exercises: ['box jump', 'jump squat', 'plyometric', 'burpee', 'running'],
    muscleGroups: [],
    substitutes: { 'box jump': 'step up', 'running': 'walking', 'burpee': 'squat' },
  },
};

// ============================================================================
// REHAB / PREHAB EXERCISES
// ============================================================================

export const PREHAB_EXERCISES: Record<InjuryLocation, {
  name: string;
  sets: number;
  reps: string;
  notes: string;
}[]> = {
  shoulder: [
    { name: 'Band Pull-Aparts', sets: 2, reps: '15', notes: 'Light band, squeeze shoulder blades' },
    { name: 'Face Pulls', sets: 2, reps: '15', notes: 'Light weight, external rotation at top' },
    { name: 'Shoulder Circles', sets: 2, reps: '10 each direction', notes: 'Slow, controlled circles' },
  ],
  elbow: [
    { name: 'Eccentric Wrist Curls', sets: 2, reps: '12', notes: 'Very light weight, slow 3s lowering phase' },
    { name: 'Pronation/Supination', sets: 2, reps: '10 each', notes: 'Light dumbbell or hammer, rotate forearm' },
    { name: 'Band Forearm Rotations', sets: 2, reps: '10 each', notes: 'Light resistance band' },
    { name: 'Elbow Flexion Isometrics', sets: 2, reps: '10s hold x 5', notes: 'Gentle contraction at 90 degrees, no pain' },
  ],
  wrist: [
    { name: 'Wrist Circles', sets: 2, reps: '10 each direction', notes: 'Slow and controlled' },
    { name: 'Prayer Stretch', sets: 2, reps: '20s hold', notes: 'Press palms together, lower to waist' },
    { name: 'Radial/Ulnar Deviation', sets: 2, reps: '10 each', notes: 'Light weight, side-to-side wrist movement' },
  ],
  hand: [
    { name: 'Finger Extensions', sets: 2, reps: '15', notes: 'Rubber band resistance' },
    { name: 'Grip Squeezes', sets: 2, reps: '10', notes: 'Soft ball or stress ball' },
  ],
  'upper-back': [
    { name: 'Cat-Cow Stretch', sets: 2, reps: '10', notes: 'Slow, breathe through movement' },
    { name: 'Thoracic Extensions', sets: 2, reps: '10', notes: 'Foam roller under upper back' },
    { name: 'Band Pull-Aparts', sets: 2, reps: '15', notes: 'Light band, retract scapulae' },
  ],
  'lower-back': [
    { name: 'Cat-Cow Stretch', sets: 2, reps: '10', notes: 'Slow, breathe through each position' },
    { name: 'Bird-Dog', sets: 2, reps: '8 each side', notes: 'Hold 3s at extension' },
    { name: 'Dead Bug', sets: 2, reps: '8 each side', notes: 'Keep lower back pressed to floor' },
    { name: 'Hip Flexor Stretch', sets: 2, reps: '30s each side', notes: 'Half-kneeling position' },
  ],
  neck: [
    { name: 'Neck Rotations', sets: 1, reps: '5 each direction', notes: 'Very slow, gentle' },
    { name: 'Chin Tucks', sets: 2, reps: '10', notes: 'Pull chin straight back, hold 3s' },
    { name: 'Isometric Neck Hold', sets: 2, reps: '5s hold x 4 directions', notes: 'Press head into hand gently — front, back, left, right' },
    { name: 'Lateral Neck Stretch', sets: 1, reps: '20s each side', notes: 'Ear toward shoulder, gentle hand pressure' },
  ],
  hip: [
    { name: 'Hip Circles', sets: 2, reps: '10 each direction', notes: 'Standing, use wall for balance' },
    { name: 'Clamshells', sets: 2, reps: '12 each side', notes: 'Light band above knees' },
    { name: 'Hip Flexor Stretch', sets: 2, reps: '30s each side', notes: 'Half-kneeling position' },
  ],
  knee: [
    { name: 'Terminal Knee Extensions', sets: 2, reps: '15', notes: 'Band behind knee, light resistance' },
    { name: 'Wall Sits', sets: 2, reps: '20-30s', notes: 'Pain-free range only' },
    { name: 'Straight Leg Raises', sets: 2, reps: '12 each leg', notes: 'Lying on back, squeeze quad' },
  ],
  ankle: [
    { name: 'Ankle Circles', sets: 2, reps: '10 each direction', notes: 'Seated, full range of motion' },
    { name: 'Calf Raises', sets: 2, reps: '15', notes: 'Bodyweight, slow eccentric' },
    { name: 'Ankle Dorsiflexion Stretch', sets: 2, reps: '30s each', notes: 'Knee over toe against wall' },
  ],
  foot: [
    { name: 'Towel Scrunches', sets: 2, reps: '15', notes: 'Scrunch towel with toes' },
    { name: 'Foot Circles', sets: 2, reps: '10 each direction', notes: 'Full range of motion' },
  ],
  chest: [
    { name: 'Doorway Chest Stretch', sets: 2, reps: '30s', notes: 'Elbow at shoulder height' },
    { name: 'Band Pull-Aparts', sets: 2, reps: '15', notes: 'Opens chest, activates rear delts' },
  ],
  quad: [
    { name: 'Quad Stretch', sets: 2, reps: '30s each side', notes: 'Standing or lying' },
    { name: 'Straight Leg Raises', sets: 2, reps: '12 each leg', notes: 'Activate quad at top' },
  ],
  hamstring: [
    { name: 'Hamstring Stretch', sets: 2, reps: '30s each side', notes: 'Lying or standing' },
    { name: 'Glute Bridge', sets: 2, reps: '12', notes: 'Bodyweight, squeeze glutes at top' },
  ],
  calf: [
    { name: 'Calf Stretch', sets: 2, reps: '30s each side', notes: 'Wall stretch, straight & bent knee' },
    { name: 'Ankle Circles', sets: 2, reps: '10 each direction', notes: 'Full range' },
  ],
  shin: [
    { name: 'Toe Raises', sets: 2, reps: '15', notes: 'Lift toes off ground repeatedly' },
    { name: 'Calf Stretch', sets: 2, reps: '30s each side', notes: 'Wall stretch' },
  ],
  general: [
    { name: 'Cat-Cow Stretch', sets: 2, reps: '10', notes: 'Gentle full body warm-up' },
    { name: 'Dead Bug', sets: 2, reps: '8 each side', notes: 'Core stability — keep lower back flat' },
    { name: 'Gentle Walking', sets: 1, reps: '5 min', notes: 'Easy pace, focus on movement quality' },
  ],
};

// ============================================================================
// POST-HAB / DOWN-REGULATION EXERCISES (by injury location)
// Appended to the recovery block when an injury is active — decompress and
// down-regulate the affected area after the session. Parallel to PREHAB_EXERCISES.
// ============================================================================

export const POSTHAB_EXERCISES: Record<InjuryLocation, {
  name: string;
  sets: number;
  reps: string;
  notes: string;
}[]> = {
  shoulder: [
    { name: 'Doorway Pec Stretch', sets: 1, reps: '30s each side', notes: 'Elbow at shoulder height, gentle lean' },
    { name: 'Cross-Body Shoulder Stretch', sets: 1, reps: '30s each side', notes: 'Draw the arm across, no pinch' },
  ],
  elbow: [
    { name: 'Wrist Flexor Stretch', sets: 1, reps: '30s each side', notes: 'Arm straight, gently pull fingers back' },
    { name: 'Triceps Stretch', sets: 1, reps: '30s each side', notes: 'Overhead, elbow bent — light' },
  ],
  wrist: [
    { name: 'Wrist Flexor Stretch', sets: 1, reps: '30s each side', notes: 'Gentle, pain-free range' },
    { name: 'Prayer Stretch', sets: 1, reps: '20s', notes: 'Palms together, lower slowly' },
  ],
  hand: [
    { name: 'Forearm Stretch', sets: 1, reps: '30s each side', notes: 'Open and stretch the fingers' },
  ],
  'upper-back': [
    { name: 'Child\'s Pose', sets: 1, reps: '45s', notes: 'Reach long, breathe into the mid-back' },
    { name: 'Thread the Needle', sets: 1, reps: '30s each side', notes: 'Rotate under, gentle t-spine' },
  ],
  'lower-back': [
    { name: 'Knees-to-Chest', sets: 1, reps: '45s', notes: 'Gently decompress the low back' },
    { name: 'Child\'s Pose', sets: 1, reps: '45s', notes: 'Reach long, relax the spine' },
  ],
  neck: [
    { name: 'Upper Trap Stretch', sets: 1, reps: '30s each side', notes: 'Ear toward shoulder, light hand assist' },
  ],
  hip: [
    { name: 'Couch Stretch', sets: 1, reps: '45s each side', notes: 'Decompress the hip flexors' },
    { name: 'Figure-4 Stretch', sets: 1, reps: '30s each side', notes: 'Ankle over knee, gentle' },
  ],
  knee: [
    { name: 'Quad Stretch', sets: 1, reps: '30s each side', notes: 'Standing or side-lying, pain-free' },
    { name: 'Hamstring Stretch', sets: 1, reps: '30s each side', notes: 'Soft knee, hinge from the hip' },
  ],
  ankle: [
    { name: 'Calf Stretch', sets: 1, reps: '30s each side', notes: 'Straight + bent knee' },
    { name: 'Soleus Stretch', sets: 1, reps: '30s each side', notes: 'Bent-knee wall stretch' },
  ],
  foot: [
    { name: 'Plantar Fascia Release', sets: 1, reps: '45s each side', notes: 'Roll the arch on a ball' },
    { name: 'Calf Stretch', sets: 1, reps: '30s each side', notes: 'Wall stretch' },
  ],
  chest: [
    { name: 'Doorway Pec Stretch', sets: 1, reps: '30s each side', notes: 'Open the chest after pressing' },
  ],
  quad: [
    { name: 'Quad Stretch', sets: 1, reps: '30s each side', notes: 'Standing or side-lying' },
    { name: 'Couch Stretch', sets: 1, reps: '45s each side', notes: 'Decompress hip flexors + quads' },
  ],
  hamstring: [
    { name: 'Hamstring Stretch', sets: 1, reps: '30s each side', notes: 'Soft knee, hinge from the hip' },
    { name: 'Figure-4 Stretch', sets: 1, reps: '30s each side', notes: 'Glute + posterior hip' },
  ],
  calf: [
    { name: 'Calf Stretch', sets: 1, reps: '30s each side', notes: 'Straight + bent knee' },
    { name: 'Soleus Stretch', sets: 1, reps: '30s each side', notes: 'Bent-knee wall stretch' },
  ],
  shin: [
    { name: 'Anterior Tib Stretch', sets: 1, reps: '30s each side', notes: 'Point the toe, gentle top-of-foot stretch' },
    { name: 'Calf Stretch', sets: 1, reps: '30s each side', notes: 'Balance the lower leg' },
  ],
  general: [
    { name: '90/90 Breathing', sets: 1, reps: '2 min', notes: 'Down-regulate — long exhales' },
  ],
};

/** Cap + dedupe by name — keep injected prep/recovery short and impactful. */
function dedupeCap<T extends { name: string }>(items: T[], cap: number): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const it of items) {
    const key = it.name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(it);
    if (out.length >= cap) break;
  }
  return out;
}

/** Short, deduped prehab set for one or more active injury locations. */
export function getInjuryPrehab(locations: string[], cap = 3) {
  return dedupeCap(
    locations.flatMap(l => PREHAB_EXERCISES[l as InjuryLocation] || []),
    cap,
  );
}

/** Short, deduped post-hab (down-regulation) set for active injury locations. */
export function getInjuryPosthab(locations: string[], cap = 3) {
  return dedupeCap(
    locations.flatMap(l => POSTHAB_EXERCISES[l as InjuryLocation] || []),
    cap,
  );
}

// ============================================================================
// RETURN-TO-TRAINING PHASE DURATIONS (days)
// ============================================================================

export const RETURN_PHASE_DURATIONS: Record<InjurySeverity, {
  rest: [number, number];
  light: [number, number];
  moderate: [number, number];
}> = {
  mild: { rest: [2, 3], light: [3, 4], moderate: [3, 4] },
  moderate: { rest: [5, 7], light: [5, 7], moderate: [5, 7] },
  severe: { rest: [7, 14], light: [7, 14], moderate: [7, 14] },
};
